import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  useAskSmetaChatMutation,
  useDeleteSmetaChatSessionMutation,
  useListSmetaChatMessagesQuery,
  useListSmetaChatSessionsQuery,
} from '@shared/api';
import type { SmetaChatMessage } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { useAgentPermission } from '@entities/user';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: ${({ theme }) => theme.spacing(5)};
  align-items: stretch;
  min-height: 540px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  min-width: 0;
`;

const SessionRow = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.backgroundElevated};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  font: inherit;
  color: inherit;
  transition: border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
`;

const SessionTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
`;

const DeleteBtn = styled.span`
  flex: none;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 16px;
  line-height: 1;
  padding: 0 ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.status.danger};
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
  min-height: 540px;
`;

const Thread = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing(5)};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Bubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 78%;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ theme, $role }) =>
    $role === 'user' ? theme.colors.primarySoft : theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3.5)};
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Empty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => theme.spacing(6)};
  font-size: 14px;
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 460px;
  line-height: 1.55;
`;

const Composer = styled.form`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: flex-end;
  padding: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

const Textarea = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 44px;
  max-height: 160px;
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const Notice = styled.div`
  padding: ${({ theme }) => theme.spacing(6)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 14px;
`;

/** ОСНОВА.Сметчик, Функция 9 — чат-ассистент поверх результатов всех функций.
 *  Многоходовой read-only диалог: слева список сессий, справа тред + ввод.
 *  Ответ приходит целиком (стрима у DeepSeek нет) — показываем индикатор. */
export function SmetaChat() {
  const { can_read } = useAgentPermission('smeta');
  const [activeId, setActiveId] = useState<string | null>(null);
  // Оптимистично показанный вопрос, на который ещё ждём ответ.
  const [draft, setDraft] = useState<string | null>(null);
  const [text, setText] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);

  const { data: sessionsData } = useListSmetaChatSessionsQuery(undefined, { skip: !can_read });
  const sessions = useMemo(() => sessionsData?.items ?? [], [sessionsData]);

  const { data: messagesData, isFetching: messagesLoading } = useListSmetaChatMessagesQuery(
    activeId ?? '',
    { skip: !activeId || !can_read },
  );
  const messages = useMemo<SmetaChatMessage[]>(() => messagesData?.items ?? [], [messagesData]);

  const [ask, { isLoading: asking }] = useAskSmetaChatMutation();
  const [deleteSession] = useDeleteSmetaChatSessionMutation();

  // Прокрутка вниз при появлении новых сообщений / индикатора ожидания.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, draft, asking]);

  // Если активная сессия исчезла (удалена) — сбросить выбор.
  useEffect(() => {
    if (activeId && sessions.length > 0 && !sessions.some((s) => s.id === activeId)) {
      setActiveId(null);
    }
  }, [sessions, activeId]);

  const onNewChat = () => {
    setActiveId(null);
    setDraft(null);
    setText('');
  };

  const onSelect = (id: string) => {
    setActiveId(id);
    setDraft(null);
  };

  const onDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await deleteSession(id).unwrap().catch(() => undefined);
    if (activeId === id) setActiveId(null);
  };

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const question = text.trim();
    if (!question || asking) return;
    setText('');
    setDraft(question);
    try {
      const res = await ask({ question, session_id: activeId ?? undefined }).unwrap();
      // При первом вопросе сессия создаётся сервером — подхватываем её id, чтобы
      // подписаться на её сообщения (инвалидация перечитает тред).
      if (!activeId) setActiveId(res.session_id);
    } catch {
      // Вернём текст в поле, чтобы пользователь мог повторить.
      setText(question);
    } finally {
      setDraft(null);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  if (!can_read) {
    return <Notice>Нет доступа к агенту ОСНОВА.Сметчик.</Notice>;
  }

  const showEmptyThread = !activeId && !draft && messages.length === 0;

  return (
    <Grid>
      <Sidebar>
        <Button $variant="primary" $block onClick={onNewChat} type="button">
          + Новый чат
        </Button>
        {sessions.map((s) => (
          <SessionRow key={s.id} $active={s.id === activeId} onClick={() => onSelect(s.id)} type="button">
            <SessionTitle>{s.title}</SessionTitle>
            <DeleteBtn
              role="button"
              aria-label="Удалить чат"
              onClick={(e) => void onDelete(e, s.id)}
            >
              ×
            </DeleteBtn>
          </SessionRow>
        ))}
        {sessions.length === 0 && (
          <Hint>Пока нет бесед. Задайте первый вопрос — сессия создастся автоматически.</Hint>
        )}
      </Sidebar>

      <Panel>
        <Thread ref={threadRef}>
          {showEmptyThread ? (
            <Empty>
              <strong>Сметчик-ассистент</strong>
              <Hint>
                Спросите про результаты ваших проверок (расценки, объёмы, КС-2/3, М-29, тендер,
                ЗВИ, реестры, ГП↔СП) или задайте методический вопрос (ФЕР/ГЭСН/НР-СП/БИМ). Например:
                «покажи мои последние проверки», «объясни расхождения в последней проверке расценок»,
                «что такое СП по приказу 774/пр».
              </Hint>
            </Empty>
          ) : (
            <>
              {messages.map((m) => (
                <Bubble key={m.id} $role={m.role === 'user' ? 'user' : 'assistant'}>
                  {m.content}
                </Bubble>
              ))}
              {draft && <Bubble $role="user">{draft}</Bubble>}
              {asking && <Bubble $role="assistant">Ассистент печатает…</Bubble>}
              {messagesLoading && messages.length === 0 && !draft && (
                <Empty>Загрузка истории…</Empty>
              )}
            </>
          )}
        </Thread>

        <Composer onSubmit={onSubmit}>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Спросите ассистента-сметчика…"
            disabled={asking}
            rows={1}
          />
          <Button $variant="primary" type="submit" disabled={asking || !text.trim()}>
            {asking ? '…' : 'Отправить'}
          </Button>
        </Composer>
      </Panel>
    </Grid>
  );
}
